import React, { Fragment, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Switch from "react-switch";
import Select from "react-select";

import Loader from "../Common/Loader";
import Paginate from "../Common/Paginate";

import { EndPoints } from "../../Helpers/Constants/EndPoints";
import API from "../../Helpers/Constants/EndPoints/Api";
import Axios from "../../Helpers/Universal/Axios/Axios";
import {
  BLOCK_STATUS,
  LIMIT,
  NA,
  OFFSET,
  USER_TYPE,
} from "../../Helpers/Constants/Default";
import DatePicker from "../Common/DatePicker";
import TransferAcc from "../Common/TransferAcc";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import DataNotFound from "../Common/DataNotFound";
import { City, State } from "country-state-city";

const Supervisors = ({ permissions: { all, add, edit, delete: del } }) => {
  const paginate = useSelector((val) => val.paginate);
  const [data, setData] = useState([]);
  const [dropdown, setDropdown] = useState({ 
    managers: [],
    managerOptions: []
  });
  
  // Separate states for each filter
  const [managerFilter, setManagerFilter] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [stateFilter, setStateFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);
  
  const [filter, setFilter] = useState({
    limit: LIMIT,
    offset: paginate?.supervisor || OFFSET,
  });
  const [visible, setVisible] = useState({ loader: false, transferAcc: false });
  const [transferAccData, setTransferAccData] = useState({
    fromUserName: "",
    toUserName: "",
    fromId: "",
  });

  // Create refs for dropdowns
  const dropdownRefs = useRef({});

  const getData = async () => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      
      // Build query parameters from individual filter states
      const params = {
        limit: filter.limit,
        offset: filter.offset
      };
      
      // Add filters only if they have values
      if (managerFilter.length > 0) {
        params.parentId = managerFilter.map(m => m.value);
      }
      
      if (searchFilter) {
        params.search = searchFilter;
      }
      
      if (stateFilter) {
        params.states = [stateFilter];
      }
      
      if (cityFilter) {
        params.cities = [cityFilter];
      }
      
      const { status, data } = await Axios.get(API.SUPERVISOR.LISTING, params);
      if (status) setData(data);
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleBlockAccess = async ({ _id, blockAccess }) => {
    try {
      const { status } = await Axios.patch(API.SUPERVISOR.UPDATE, {
        supervisorId: _id,
        blockAccess: !blockAccess,
      });
      if (status)
        setData((prev) => ({
          ...prev,
          data: prev?.data?.map((val) =>
            val._id === _id ? { ...val, blockAccess: !blockAccess } : val
          ),
        }));
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const handleDelete = async (supervisorId) => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      await Axios.delete(API.SUPERVISOR.DELETE + "/" + supervisorId);
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleResetPassword = async (supervisorId) => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      await Axios.get(API.SUPERVISOR.RESET_PASSWORD + "/" + supervisorId);
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const getDropdowns = async () => {
    try {
      const { data, status } = await Axios.get(API.DROPDOWN.USER, {
        accountType: USER_TYPE.MANAGER,
      });

      if (status) {
        const managerOptions = data.map((manager) => ({
          value: manager._id,
          label: manager.name,
        }));
        
        setDropdown((prev) => ({ 
          ...prev, 
          managers: data,
          managerOptions 
        }));
      }
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const exportExcel = async () => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      
      // Build params for export the same way as getData
      const params = {};
      
      if (managerFilter.length > 0) {
        params.parentId = managerFilter.map(m => m.value);
      }
      
      if (searchFilter) {
        params.search = searchFilter;
      }
      
      if (stateFilter) {
        params.states = [stateFilter];
      }
      
      if (cityFilter) {
        params.cities = [cityFilter];
      }
      
      const { status, data } = await Axios.get(API.REPORT.SUPERVISOR, params);

      if (status) window.open(data, "_blank");
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleAction = (supervisorId, e) => {
    e.stopPropagation();
    setData((prev) => ({
      ...prev,
      data: prev?.data?.map((val) =>
        val._id === supervisorId && !val?.action
          ? { ...val, action: true }
          : { ...val, action: false }
      ),
    }));
  };

  // Close all dropdowns when clicking outside
  const handleClickOutside = (e) => {
    // Check if click is outside any dropdown
    const isOutside = Object.values(dropdownRefs.current).every(ref => {
      return ref && !ref.contains(e.target);
    });
    
    if (isOutside) {
      setData((prev) => ({
        ...prev,
        data: prev?.data?.map((val) => ({
          ...val,
          action: false,
        })),
      }));
    }
  };

  useEffect(() => {
    // Add event listener when component mounts
    document.addEventListener("mousedown", handleClickOutside);
    
    // Remove event listener when component unmounts
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBlockUnblock = async ({ _id, isBlocked }) => {
    try {
      Swal.fire({
        title: `${isBlocked ? "Block" : "Un-block"} Supervisor`,
        text: `Are you sure you want to ${isBlocked ? "Block" : "Un-block"}?`,
        confirmButtonText: "Yes",
        showCancelButton: true,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const { status } = await Axios.patch(API.SUPERVISOR.UPDATE, {
            supervisorId: _id,
            isBlocked: !isBlocked,
          });
          if (status)
            setData((prev) => ({
              ...prev,
              data: prev?.data?.map((val) =>
                val._id === _id ? { ...val, isBlocked: !isBlocked } : val
              ),
            }));
        }
      });
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const handleTransferAcc = (val) => {
    setTransferAccData((prev) => ({
      ...prev,
      fromUserName: val.userName,
      transferFromId: val._id,
      agentCount: val.agentCount,
    }));
    setVisible((prev) => ({ ...prev, transferAcc: true }));
  };

  const [states, setStatesAndUTs] = useState([]);
  const [cities, setCities] = useState([]);
  
  useEffect(() => {
    setStatesAndUTs(State.getStatesOfCountry("IN"));
  }, []);

  const handleStateChange = (selectedOption) => {
    if (!selectedOption) {
      setStateFilter(null);
      setCities([]);
      setCityFilter(null);
      return;
    }

    const stateCode = selectedOption.label;
    setStateFilter(stateCode);
    
    const stateCities = City.getCitiesOfState("IN",  selectedOption.label);
    setCities(stateCities);
    setCityFilter(null); // Reset city filter when state changes
  };

  const handleCityChange = (selectedOption) => {
    if (!selectedOption) {
      setCityFilter(null);
      return;
    }
    
    setCityFilter(selectedOption.value);
  };

  const handleSearchChange = (e) => {
    setSearchFilter(e.target.value);
  };

  const handleManagerChange = (selectedOptions) => {
    setManagerFilter(selectedOptions || []);
  };

  // Auto-refresh data when any filter changes
  useEffect(() => {
    // Reset to first page when filters change
    setFilter(prev => ({...prev, offset: 0}));
  }, [managerFilter, searchFilter, stateFilter, cityFilter]);

  // Get data when filter changes (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getData();
    }, searchFilter ? 500 : 0); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [filter.offset, filter.limit, managerFilter, searchFilter, stateFilter, cityFilter]);

  useEffect(() => {
    getDropdowns();
  }, []);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "42px",
      backgroundColor: "#2d3748",
      borderColor: state.isFocused ? "#63b3ed" : "#4a5568",
      boxShadow: state.isFocused ? "0 0 0 1px #63b3ed" : provided.boxShadow,
      color: "white",
      "&:hover": {
        borderColor: "#63b3ed"
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "white",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#a0aec0",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#2d3748",
      zIndex: 9999
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: "#2d3748",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#4a5568"
        : state.isFocused
        ? "#374151"
        : "#2d3748",
      color: "white",
      "&:active": {
        backgroundColor: "#4a5568"
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#4a5568",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "white",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "white",
      ":hover": {
        backgroundColor: "#e53e3e",
        color: "white",
      },
    }),
    input: (provided) => ({
      ...provided,
      color: "white",
    })
  };

  // Create options for states and cities
  const stateOptions = states.map(state => ({
    value: state.isoCode,
    label: state.name
  }));

  const cityOptions = cities.map(city => ({
    value: city.name,
    label: city.name
  }));

  return (
    <Fragment>
      {visible.transferAcc && (
        <TransferAcc
          setVisible={setVisible}
          transferAccData={transferAccData}
          setTransferAccData={setTransferAccData}
          setData={setData}
        />
      )}
      {visible.loader && <Loader />}
      <div className="container-fluid py-3">
        <div className="dateselect_navigation d-flex justify-content-between align-items-center">
          <div className="d-flex main_bar">
            {/* Manager Select - Multi Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={managerFilter}
                options={dropdown.managerOptions}
                onChange={handleManagerChange}
                placeholder="Select Manager"
                isClearable
                isMulti
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Search Box */}
            <div className="d-flex position-relative bg-gradient-dark rounded mx-2" style={{ height: "42px" }}>
              <i
                className="fa fa-search position-absolute top-50 translate-middle-y ms-3 text-white"
                style={{ zIndex: 10 }}
                aria-hidden="true"
              ></i>
              <input
                value={searchFilter}
                onChange={handleSearchChange}
                type="search"
                className="text-start form-control bg-dark text-white border-0 ps-5 h-100"
                style={{ 
                  cursor: "unset", 
                  width: "200px",
                  position: "relative"
                }}
                name="gsearch"
                placeholder="Search..."
              />
            </div>
            
            {/* State Select - Single Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={stateOptions.find(opt => opt.value === stateFilter) || null}
                options={stateOptions}
                onChange={handleStateChange}
                placeholder="Select State"
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            
            {/* City Select - Single Select */}
            <div className="mx-2" style={{ minWidth: "200px" }}>
              <Select
                value={cityOptions.find(opt => opt.value === cityFilter) || null}
                options={cityOptions}
                onChange={handleCityChange}
                placeholder="Select City"
                isClearable
                styles={customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={!stateFilter}
              />
            </div>
          </div>
          <div className="d-flex align-items-center">
            <div className="me-2  mob-margin-none">
              {(all || add) && (
                <Link to={EndPoints.ADD_SUPERVISOR}>
                  <button className="btn bg-gradient-dark float-end d-flex btn-md mb-1 h-52 align-items-center">
                    <i className="fa fa-plus me-2 mt-1" aria-hidden="true"></i>
                    Add
                  </button>
                </Link>
              )}
            </div>
            <button
              className="btn bg-gradient-dark float-end d-flex btn-md mb-1 align-items-center h-52 data-del"
              onClick={exportExcel}
            >
              <i className="fa fa-download me-2 mt-1" aria-hidden="true"></i>
              Export
            </button>
          </div>
        </div>
        {data?.data?.length === 0 ? (
          <DataNotFound />
        ) : (
          <Fragment>
            <div className="overflow-table">
              <table className="page-table">
                <thead>
                  <tr>
                    <th scope="col" className="text-uppercase">
                      #
                    </th>
                    <th scope="col" className="text-uppercase">
                      Profile
                    </th>
                    <th scope="col" className="text-uppercase">
                      User Id
                    </th>
                    <th scope="col" className="text-uppercase">
                      Name
                    </th>
                    <th scope="col" className="text-uppercase">
                      Manager
                    </th>
                    <th scope="col" className="text-uppercase">
                      No. of Agents
                    </th>
                    {/* <th scope="col" className="text-uppercase">
                      Transfer
                    </th> */}
                    {(all || edit || del) && (
                      <th scope="col" className="text-uppercase">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.map((val, i) => (
                    <tr key={val._id}>
                      <td>{++i + filter.offset}</td>
                      <td>
                        {val.image ? (
                          <img
                            className="profile"
                            src={val.image}
                            alt={val.name}
                          />
                        ) : (
                          NA
                        )}
                      </td>
                      <td>
                        <Link to={EndPoints.SUPERVISOR} state={val._id}>
                          {val.userName}
                        </Link>
                      </td>
                      <td>{val.name}</td>
                      <td>{val.parentId?.name || NA}</td>
                      <td>{val.agentCount}</td>
                      {/* <td>
                        {val.transferAcc ? (
                          <i
                            className="fa fa-life-ring fs-6 text-danger pe-auto"
                            disabled={true}
                          ></i>
                        ) : (
                          <i
                            onClick={() => handleTransferAcc(val)}
                            className="fa fa-life-ring fs-6 text-success pe-auto"
                          ></i>
                        )}
                      </td> */}
                      {(all || edit || del) && (
                        <td>
                          <div
                            className="position-relative"
                            ref={(el) => (dropdownRefs.current[val._id] = el)}
                          >
                            <i
                              className="material-icons ps-2 cursior-pointer"
                              onClick={(e) => handleAction(val._id, e)}
                            >
                              more_horiz
                            </i>
                            <div className="more-ic">
                              <ul
                                className="icon-list"
                                style={{
                                  display: val?.action ? "block" : "none",
                                }}
                              >
                                <li onClick={() => handleBlockUnblock(val)}>
                                  {val.isBlocked
                                    ? BLOCK_STATUS.UN_BLOCKED
                                    : BLOCK_STATUS.BLOCKED}
                                </li>
                                <li onClick={() => handleDelete(val._id)}>
                                  Delete Data
                                </li>
                                <li>
                                  <Link
                                    to={EndPoints.EDIT_SUPERVISOR}
                                    state={{
                                      ...val,
                                      password: val.dcryptedPass,
                                      parentId: val.parentId?._id,
                                    }}
                                  >
                                    Edit
                                  </Link>
                                </li>
                                <li>
                                  {val.transferAcc ? (
                                    <span
                                      className=" fs-6 text-danger "
                                      disabled={true}
                                    ></span>
                                  ) : (
                                    <span
                                      onClick={() => handleTransferAcc(val)}
                                      className=" fs-6 text-success "
                                    > Transfer</span>
                                  )}
                                </li>
                              </ul>
                            </div>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginate
              count={data?.count}
              filter={filter}
              setFilter={setFilter}
              sliceKey={"supervisor"}
              forcePage={paginate?.supervisor / 10}
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default Supervisors;